import { create } from 'zustand';

export interface ContentUtilizationFilters {
  titleUtilization: boolean;
  titleOverLimit: boolean;
  itemHighlightsUtilization: boolean;
  bulletUtilization: boolean;
  descriptionUtilization: boolean;
  imageUtilization: boolean;
  videoUtilization: boolean;
  enhancedUtilization: boolean;
}

interface ContentUtilizationFilterState {
  isOpen: boolean;
  filters: ContentUtilizationFilters;
  tempFilters: ContentUtilizationFilters;
  toggleFilter: (key: keyof ContentUtilizationFilters, value: boolean) => void;
  setTempFilter: (key: keyof ContentUtilizationFilters, value: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
  resetFilters: () => void;
  applyFilters: () => void;
  setAllFilters: (value: boolean) => void;
  getActiveFilters: () => string[];
}

const defaultFilters: ContentUtilizationFilters = {
  titleUtilization: false,
  titleOverLimit: false,
  itemHighlightsUtilization: false,
  bulletUtilization: false,
  descriptionUtilization: false,
  imageUtilization: false,
  videoUtilization: false,
  enhancedUtilization: false
};

const FilterNames = {
  titleUtilization: 'title',
  titleOverLimit: 'title over 75 characters',
  itemHighlightsUtilization: 'item highlights',
  bulletUtilization: 'bullet',
  descriptionUtilization: 'description',
  imageUtilization: 'image',
  videoUtilization: 'video',
  enhancedUtilization: 'enhanced'
};

export const useContentUtilizationStore = create<ContentUtilizationFilterState>((set, get) => ({
  isOpen: false,
  filters: { ...defaultFilters },
  tempFilters: { ...defaultFilters },
  setTempFilter: (key, value) => set((state) => ({ tempFilters: { ...state.tempFilters, [key]: value } })),
  toggleFilter: (key, value) => {
    set(() => ({ filters: { ...defaultFilters, [key]: value } }));
    set(() => ({ tempFilters: { ...defaultFilters, [key]: value } }));
  },
  setAllFilters: (value) => {
    set((state) => ({
      filters: {
        ...state.filters,
        titleUtilization: value,
        titleOverLimit: value,
        itemHighlightsUtilization: value,
        bulletUtilization: value,
        descriptionUtilization: value,
        imageUtilization: value,
        videoUtilization: value,
        enhancedUtilization: value
      }
    }));
    set((state) => ({
      tempFilters: {
        ...state.tempFilters,
        titleUtilization: value,
        titleOverLimit: value,
        itemHighlightsUtilization: value,
        bulletUtilization: value,
        descriptionUtilization: value,
        imageUtilization: value,
        videoUtilization: value,
        enhancedUtilization: value
      }
    }));
  },
  openModal: () =>
    set((state) => ({
      isOpen: true,
      tempFilters: { ...state.filters }
    })),
  closeModal: () => set({ isOpen: false }),
  resetFilters: () =>
    set(() => ({
      tempFilters: { ...defaultFilters },
      filters: { ...defaultFilters }
    })),
  applyFilters: () =>
    set((state) => ({
      filters: { ...state.tempFilters },
      isOpen: false
    })),
  getActiveFilters: () => {
    // return the readable filter names for the active filters. Eg. ['title', 'bullet', ...]
    const filters = get().filters;
    return Object.entries(filters)
      .filter(([_, isActive]) => isActive)
      .map(([key, _]) => FilterNames[key as keyof ContentUtilizationFilters]);
  }
}));
